import { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { listAdminDonations, fetchAdminCsv } from "../../lib/adminApi";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const formatAmount = (amount, currency = "NGN") => {
  if (typeof amount !== "number") return "-";
  return `${currency} ${amount.toLocaleString()}`;
};

const Donations = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAdminDonations({ limit: 1000, skip: 0 });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Failed to load donations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((x) =>
      [x.name, x.email, x.currency, x.purpose, String(x.amount), x.is_anonymous ? "anonymous" : ""]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [items, q]);

  const total = useMemo(() => {
    // mixed currencies possible; this is count total by rows only, not converted total
    return filtered.reduce((acc, x) => acc + (Number(x.amount) || 0), 0);
  }, [filtered]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await fetchAdminCsv("/admin/donations/export.csv");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "donations.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Donations CSV exported");
    } catch (err) {
      toast.error(err.message || "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Donation Pledges">
      <div className="space-y-6" data-testid="admin-donations-page">
        <Card>
          <CardHeader className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle>Donations ({filtered.length})</CardTitle>
              <div className="flex gap-3">
                <Input
                  placeholder="Search donations..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="w-72"
                />
                <Button variant="outline" onClick={load} disabled={loading}>
                  Refresh
                </Button>
                <Button onClick={handleExport} disabled={exporting}>
                  {exporting ? "Exporting..." : "Export CSV"}
                </Button>
              </div>
            </div>
            <p className="text-sm text-stone-600">
              Sum of visible rows (not currency-converted): <span className="font-semibold">{total.toLocaleString()}</span>
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-stone-500">Loading donations...</p>
            ) : filtered.length === 0 ? (
              <p className="text-stone-500">No donations yet.</p>
            ) : (
              <div className="space-y-4">
                {filtered.map((item) => (
                  <div key={item.id} className="rounded-xl border border-stone-200 p-4">
                    <div className="flex flex-col md:flex-row md:justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-stone-900">
                          {item.is_anonymous ? "Anonymous Donor" : item.name}
                        </h3>
                        <p className="text-sm text-stone-600">
                          {item.is_anonymous ? "-" : item.email}
                        </p>
                      </div>
                      <span className="text-xs text-stone-500">{formatDate(item.created_at)}</span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm">
                      <p><span className="font-medium">Amount:</span> {formatAmount(item.amount, item.currency)}</p>
                      <p><span className="font-medium">Purpose:</span> {item.purpose}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Donations;
